import { useCallback, useEffect, useState } from "react";
import { Copy, Link2, Loader2, RefreshCw, Shield, Ticket, Trash2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * AdminPartnerAccessTile
 *
 * Director K's self-service console for the Partner Dashboard access layer:
 *   1. Partner allowlist · list, add, remove
 *   2. Invite links · generate pre-approved signup URLs
 *   3. Owner roster · view who holds super-admin on the Partner Dashboard
 *
 * Backed by:
 *   GET/POST/DELETE /api/admin/partner-allowlist
 *   GET/POST/DELETE /api/admin/partner-invites
 *   GET            /api/admin/partner-owners
 */
export default function AdminPartnerAccessTile() {
    const [tab, setTab] = useState("allowlist"); // allowlist | invites | owners
    const [allowlist, setAllowlist] = useState([]);
    const [invites, setInvites] = useState([]);
    const [owners, setOwners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newEmail, setNewEmail] = useState("");
    const [newInviteEmail, setNewInviteEmail] = useState("");
    const [inviteDays, setInviteDays] = useState(14);
    const [working, setWorking] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [a, i, o] = await Promise.all([
                api.get("/admin/partner-allowlist"),
                api.get("/admin/partner-invites"),
                api.get("/admin/partner-owners"),
            ]);
            setAllowlist(a.data.allowlist || []);
            setInvites(i.data.invites || []);
            setOwners(o.data.owners || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load partner access data");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const addAllowlist = async (e) => {
        e.preventDefault();
        if (!newEmail.trim()) return;
        setWorking(true);
        try {
            await api.post("/admin/partner-allowlist", { email: newEmail.trim().toLowerCase() });
            toast.success(`${newEmail.trim().toLowerCase()} added to allowlist`);
            setNewEmail("");
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to add email");
        } finally {
            setWorking(false);
        }
    };

    const removeAllowlist = async (email) => {
        if (!window.confirm(`Remove ${email} from the allowlist? They won't be able to sign in.`)) return;
        try {
            await api.delete(`/admin/partner-allowlist/${encodeURIComponent(email)}`);
            toast.success(`${email} removed`);
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to remove email");
        }
    };

    const createInvite = async (e) => {
        e.preventDefault();
        if (!newInviteEmail.trim()) return;
        setWorking(true);
        try {
            const { data } = await api.post("/admin/partner-invites", {
                email: newInviteEmail.trim().toLowerCase(),
                expires_days: Number(inviteDays) || 14,
            });
            await navigator.clipboard?.writeText(data.invite_url).catch(() => {});
            toast.success("Invite created — link copied to clipboard");
            setNewInviteEmail("");
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to create invite");
        } finally {
            setWorking(false);
        }
    };

    const revokeInvite = async (token) => {
        if (!window.confirm("Revoke this invite link?")) return;
        try {
            await api.delete(`/admin/partner-invites/${token}`);
            toast.success("Invite revoked");
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to revoke");
        }
    };

    const copyInvite = (url) => {
        navigator.clipboard?.writeText(url);
        toast.success("Invite link copied");
    };

    return (
        <section
            data-testid="admin-partner-access-tile"
            className="mt-10 p-6 bg-cream-warm rounded-sm border border-forest/10"
        >
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                <div>
                    <span className="divider-rule text-brown">
                        <Shield size={12} /> Partner Access · Owner Console
                    </span>
                    <h2 className="font-serif text-3xl text-forest mt-3">
                        Allowlist, Invites &amp; Owners
                    </h2>
                    <p className="text-xs text-brown mt-1">
                        Add or remove partner emails yourself. Generate pre-approved invite links. All changes are instant — no deploy needed.
                    </p>
                </div>
                <button
                    onClick={refresh}
                    className="btn-pill btn-ghost text-[0.65rem]"
                    data-testid="admin-partner-access-refresh"
                >
                    <RefreshCw size={12} /> Refresh
                </button>
            </div>

            <div className="flex gap-2 flex-wrap mb-5" role="tablist">
                {[
                    { k: "allowlist", label: `Allowlist (${allowlist.length})`, testid: "tab-allowlist" },
                    { k: "invites", label: `Invites (${invites.filter(i => i.valid).length})`, testid: "tab-invites" },
                    { k: "owners", label: `Owners (${owners.length})`, testid: "tab-owners" },
                ].map((t) => (
                    <button
                        key={t.k}
                        type="button"
                        role="tab"
                        aria-selected={tab === t.k}
                        onClick={() => setTab(t.k)}
                        data-testid={`admin-partner-${t.testid}`}
                        className={`px-4 py-2 text-[0.65rem] tracking-[0.28em] uppercase rounded-sm border transition-colors ${
                            tab === t.k
                                ? "bg-forest text-cream border-forest"
                                : "bg-cream text-forest/70 border-forest/15 hover:text-forest"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-brown text-sm py-6">
                    <Loader2 size={14} className="animate-spin" /> Loading…
                </div>
            ) : tab === "allowlist" ? (
                <div>
                    <form onSubmit={addAllowlist} className="flex flex-wrap gap-2 mb-4" data-testid="admin-allowlist-form">
                        <input
                            type="email"
                            required
                            placeholder="partner@example.com"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            data-testid="admin-allowlist-email-input"
                            className="flex-1 min-w-[240px] px-4 py-2.5 bg-cream border border-forest/15 rounded-sm text-forest text-sm focus:outline-none focus:border-gold"
                        />
                        <button
                            type="submit"
                            disabled={working}
                            data-testid="admin-allowlist-add-btn"
                            className="btn-pill btn-gold text-[0.65rem] disabled:opacity-60"
                        >
                            <UserPlus size={12} /> Add to allowlist
                        </button>
                    </form>
                    <div className="divide-y divide-forest/10 border border-forest/10 rounded-sm bg-cream" data-testid="admin-allowlist-table">
                        {allowlist.length === 0 ? (
                            <div className="p-4 text-brown text-sm">No allowlist entries.</div>
                        ) : allowlist.map((row) => (
                            <div key={row.email} className="flex items-center justify-between gap-3 px-4 py-3">
                                <div className="min-w-0">
                                    <div className="font-medium text-forest text-sm truncate">
                                        {row.email}
                                        {row.is_owner && (
                                            <span className="ml-2 text-[0.6rem] tracking-[0.25em] uppercase text-forest bg-gold/30 px-2 py-0.5 rounded-full">
                                                Owner
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[0.65rem] text-brown mt-0.5">
                                        {row.has_account ? "Account created" : "Pre-approved · no account yet"}
                                        {row.has_signed_agreement && " · Agreement signed"}
                                        {row.source && row.source !== "seed" && ` · via ${row.source}`}
                                    </div>
                                </div>
                                <button
                                    onClick={() => removeAllowlist(row.email)}
                                    disabled={row.is_owner}
                                    title={row.is_owner ? "Owner cannot be removed" : "Remove from allowlist"}
                                    data-testid={`admin-allowlist-remove-${row.email}`}
                                    className="btn-pill btn-ghost text-[0.6rem] disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <Trash2 size={11} /> Remove
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            ) : tab === "invites" ? (
                <div>
                    <form onSubmit={createInvite} className="flex flex-wrap gap-2 mb-4" data-testid="admin-invite-form">
                        <input
                            type="email"
                            required
                            placeholder="invitee@example.com"
                            value={newInviteEmail}
                            onChange={(e) => setNewInviteEmail(e.target.value)}
                            data-testid="admin-invite-email-input"
                            className="flex-1 min-w-[220px] px-4 py-2.5 bg-cream border border-forest/15 rounded-sm text-forest text-sm focus:outline-none focus:border-gold"
                        />
                        <input
                            type="number"
                            min={1}
                            max={60}
                            value={inviteDays}
                            onChange={(e) => setInviteDays(e.target.value)}
                            data-testid="admin-invite-days-input"
                            className="w-24 px-3 py-2.5 bg-cream border border-forest/15 rounded-sm text-forest text-sm focus:outline-none focus:border-gold"
                        />
                        <button
                            type="submit"
                            disabled={working}
                            data-testid="admin-invite-create-btn"
                            className="btn-pill btn-gold text-[0.65rem] disabled:opacity-60"
                        >
                            <Ticket size={12} /> Create invite
                        </button>
                    </form>
                    <p className="text-[0.65rem] text-brown mb-3">
                        Expires in <strong>{inviteDays} day(s)</strong>. Invitee lands directly in signup — allowlist step is skipped.
                    </p>
                    <div className="divide-y divide-forest/10 border border-forest/10 rounded-sm bg-cream" data-testid="admin-invites-table">
                        {invites.length === 0 ? (
                            <div className="p-4 text-brown text-sm">No invites yet.</div>
                        ) : invites.map((inv) => (
                            <div key={inv.token} className="px-4 py-3">
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="min-w-0">
                                        <div className="font-medium text-forest text-sm truncate">
                                            {inv.email}
                                            <span className={`ml-2 text-[0.6rem] tracking-[0.25em] uppercase px-2 py-0.5 rounded-full ${
                                                inv.valid ? "bg-olive/25 text-forest"
                                                    : inv.status === "already_used" ? "bg-forest/15 text-forest/70"
                                                    : "bg-[#b14c3c]/20 text-[#7a2a1e]"
                                            }`}>
                                                {inv.valid ? "Valid"
                                                    : inv.status === "already_used" ? "Used"
                                                    : inv.status === "expired" ? "Expired"
                                                    : inv.status === "revoked" ? "Revoked"
                                                    : inv.status}
                                            </span>
                                        </div>
                                        <div className="text-[0.65rem] text-brown mt-0.5">
                                            Expires {inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : "—"}
                                            {inv.used_by_email && ` · Used by ${inv.used_by_email}`}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => copyInvite(inv.invite_url)}
                                            data-testid={`admin-invite-copy-${inv.token}`}
                                            className="btn-pill btn-ghost text-[0.6rem]"
                                            disabled={!inv.valid}
                                        >
                                            <Copy size={11} /> Copy link
                                        </button>
                                        {inv.valid && (
                                            <button
                                                onClick={() => revokeInvite(inv.token)}
                                                data-testid={`admin-invite-revoke-${inv.token}`}
                                                className="btn-pill btn-ghost text-[0.6rem]"
                                            >
                                                <X size={11} /> Revoke
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {inv.valid && (
                                    <div className="mt-2 flex items-center gap-2 text-[0.65rem] text-brown bg-cream-warm rounded-sm border border-forest/10 px-3 py-2">
                                        <Link2 size={11} className="shrink-0 text-gold" />
                                        <code className="truncate text-forest" data-testid={`admin-invite-url-${inv.token}`}>
                                            {inv.invite_url}
                                        </code>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="divide-y divide-forest/10 border border-forest/10 rounded-sm bg-cream" data-testid="admin-owners-table">
                    {owners.length === 0 ? (
                        <div className="p-4 text-brown text-sm">No owners configured.</div>
                    ) : owners.map((o) => (
                        <div key={o.email} className="flex items-center justify-between gap-3 px-4 py-3">
                            <div>
                                <div className="font-medium text-forest text-sm">
                                    {o.email}
                                    <span className="ml-2 text-[0.6rem] tracking-[0.25em] uppercase text-forest bg-gold/30 px-2 py-0.5 rounded-full">
                                        {o.role || "Owner"}
                                    </span>
                                </div>
                                <div className="text-[0.65rem] text-brown mt-0.5">
                                    Granted {o.granted_at ? new Date(o.granted_at).toLocaleDateString() : "—"}
                                    {o.note && ` · ${o.note}`}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}
